// src/PaymentModal.js
import React from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY, STRIPE_PRICE_IDS, CREDIT_PACKAGES } from './stripeConfig';
import translations from './translations';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const PaymentModal = ({ isOpen, onClose, language = 'en', onPurchaseSuccess }) => {
  const [loading, setLoading] = React.useState(false);
  const [selectedPackage, setSelectedPackage] = React.useState('pack10');
  
  // Get translations
  const t = translations[language]?.payment || translations['en'].payment;

  if (!isOpen) return null;

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const stripe = await stripePromise;
      
      // Create checkout session
      const { error } = await stripe.redirectToCheckout({
        lineItems: [{ 
          price: STRIPE_PRICE_IDS[selectedPackage], 
          quantity: 1 
        }],
        mode: 'payment',
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&package=${selectedPackage}&lang=${language}`,
        cancelUrl: window.location.href,
        locale: language === 'ja' ? 'ja' : language === 'ru' ? 'ru' : 'en'
      });

      if (error) {
        console.error('Stripe error:', error);
        alert(t.errorMessage || 'Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      alert(t.errorMessage || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>
        
        <h2>{t.title}</h2>
        <p className="subtitle">{t.subtitle}</p>
        
        <div className="packages-grid">
          {CREDIT_PACKAGES.map((pkg) => (
            <div 
              key={pkg.id}
              className={`package-card ${selectedPackage === pkg.id ? 'selected' : ''} ${pkg.popular ? 'popular' : ''}`}
              onClick={() => setSelectedPackage(pkg.id)}
            >
              {pkg.popular && <div className="popular-badge">{t.popularBadge}</div>}
              
              <div className="credits-count">{pkg.credits}</div>
              <div className="credits-label">{t.credits}</div>
              
              <div className="price">
                ${pkg.price}
                {pkg.savings > 0 && (
                  <span className="savings">{t.save} {pkg.savings}%</span>
                )}
              </div>
              
              <div className="per-unlock">
                ${(pkg.price / pkg.credits).toFixed(2)} {t.perUnlock}
              </div>
            </div>
          ))}
        </div>
        
        <button 
          className="purchase-button"
          onClick={handlePurchase}
          disabled={loading}
        >
          {loading ? t.processing : t.purchaseButton}
        </button>
        
        <p className="secure-payment">{t.securePayment}</p>
      </div>
      
      <style>{`
        .payment-modal-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.6);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 20px;
        }

        .payment-modal {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 28px;
          max-width: 640px; width: 100%;
          max-height: 90vh; overflow-y: auto; position: relative;
          color: #1f2937; box-shadow: 0 20px 40px rgba(0,0,0,0.12), 0 4px 10px rgba(0,0,0,0.06);
        }

        .close-button { position: absolute; top: 12px; right: 12px; background: transparent; border: 1px solid #e5e7eb; font-size: 22px; cursor: pointer; color: #374151; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 10px; transition: background .2s, color .2s; }
        .close-button:hover { background: #f3f4f6; color: #111827; }
        .close-button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }

        h2 { text-align: center; color: #111827; margin: 4px 0 8px; font-size: 26px; font-weight: 700; }
        .subtitle { text-align: center; margin-bottom: 22px; color: #6b7280; }

        .packages-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 16px; margin-bottom: 22px; }

        .package-card { background: #ffffff; border-radius: 12px; padding: 18px; text-align: center; cursor: pointer; border: 1.5px solid #e5e7eb; transition: transform .2s, box-shadow .2s, border-color .2s; position: relative; }
        .package-card:hover { transform: translateY(-3px); box-shadow: 0 12px 20px rgba(0,0,0,0.08); }
        .package-card.selected { border-color: #2563eb; box-shadow: 0 12px 22px rgba(37,99,235,0.12); }
        .package-card.popular { border-color: #f59e0b; }

        .popular-badge { position: absolute; top: -10px; right: -10px; background: #f59e0b; color: white; padding: 4px 10px; border-radius: 9999px; font-size: 12px; font-weight: 700; }

        .credits-count { font-size: 34px; font-weight: 800; color: #2563eb; margin-bottom: 4px; }
        .credits-label { font-size: 13px; margin-bottom: 12px; color: #6b7280; }
        .price { font-size: 22px; font-weight: 800; margin-bottom: 2px; color: #111827; }
        .savings { display: block; font-size: 13px; color: #16a34a; margin-top: 4px; font-weight: 600; }
        .per-unlock { font-size: 12px; color: #6b7280; margin-top: 6px; }

        .purchase-button { width: 100%; padding: 14px; background: #2563eb; color: white; border: none; border-radius: 10px; font-size: 16px; font-weight: 700; cursor: pointer; transition: background .2s; }
        .purchase-button:hover:not(:disabled) { background: #1d4ed8; }
        .purchase-button:disabled { opacity: 0.7; cursor: not-allowed; }
        .purchase-button:focus-visible { outline: 2px solid #2563eb; outline-offset: 2px; }

        .secure-payment { text-align: center; font-size: 12px; color: #6b7280; margin-top: 12px; }

        @media (max-width: 600px) {
          .payment-modal { padding: 22px; }
          .packages-grid { grid-template-columns: repeat(2, 1fr); }
          .credits-count { font-size: 28px; }
          .price { font-size: 20px; }
        }

        /* RTL support */
        [dir="rtl"] .close-button { left: 12px; right: auto; }
        [dir="rtl"] .popular-badge { left: -10px; right: auto; }
      `}</style>
    </div>
  );
};

export default PaymentModal;